import { FC, useMemo } from 'react';
import { useRouter } from 'next/router';
import {
  ProductGroupMember,
  ProductGroupOptionType,
} from 'types/Product.types';
import { ProductVariantGroupSelectorProps } from './ProductVariantGroupSelector.types';
import * as Styled from './ProductVariantGroupSelector.styles';

interface OptionValueEntry {
  id: number;
  presentation: string;
  imageUrl: string;
  isActive: boolean;
  isDisabled: boolean;
}

const ProductVariantGroupSelector: FC<ProductVariantGroupSelectorProps> = ({
  productGroup,
  userCountry,
  basePath = '/product',
}) => {
  const router = useRouter();

  const availableMembers = useMemo(() => {
    if (!userCountry) return productGroup.members;
    return productGroup.members.filter(
      member =>
        !member.allowedCountries ||
        member.allowedCountries.includes(userCountry)
    );
  }, [productGroup.members, userCountry]);

  const currentMember = availableMembers.find(m => m.current);

  const currentSelections = useMemo(() => {
    if (!currentMember) return new Map<number, number>();
    const map = new Map<number, number>();
    currentMember.optionValues.forEach(ov => {
      map.set(ov.optionTypeId, ov.id);
    });
    return map;
  }, [currentMember]);

  const getUniqueValuesForOptionType = (
    optionType: ProductGroupOptionType
  ): OptionValueEntry[] => {
    const seen = new Map<number, string>();
    const sortedMembers = [...availableMembers].sort(
      (a, b) => a.position - b.position
    );

    // Collect unique option value IDs for this option type
    const uniqueValueIds: number[] = [];
    sortedMembers.forEach(member => {
      const ov = member.optionValues.find(
        v => v.optionTypeId === optionType.id
      );
      if (ov && !seen.has(ov.id)) {
        seen.set(ov.id, ov.presentation);
        uniqueValueIds.push(ov.id);
      }
    });

    // For each unique value, find best member image:
    // prefer member matching current selections on other axes
    return uniqueValueIds.map(valueId => {
      const candidates = sortedMembers.filter(m =>
        m.optionValues.some(
          ov => ov.optionTypeId === optionType.id && ov.id === valueId
        )
      );

      // Best match: member where all OTHER option types match current selections
      const bestMatch = candidates.find(m =>
        m.optionValues.every(
          ov =>
            ov.optionTypeId === optionType.id ||
            currentSelections.get(ov.optionTypeId) === ov.id
        )
      );

      const representative = bestMatch ?? candidates[0];

      // Check if selecting this value has a valid combination
      const isActive = currentSelections.get(optionType.id) === valueId;
      const isDisabled = !isActive && !bestMatch;

      return {
        id: valueId,
        presentation: seen.get(valueId) ?? '',
        imageUrl: representative.imageUrl,
        isActive,
        isDisabled,
      };
    });
  };

  const findMemberBySelections = (
    optionTypeId: number,
    selectedValueId: number
  ): ProductGroupMember | undefined => {
    const newSelections = new Map(currentSelections);
    newSelections.set(optionTypeId, selectedValueId);

    return availableMembers.find(member =>
      member.optionValues.every(
        ov => newSelections.get(ov.optionTypeId) === ov.id
      )
    );
  };

  const handleOptionClick = (optionTypeId: number, valueId: number) => {
    if (currentSelections.get(optionTypeId) === valueId) return;

    const targetMember = findMemberBySelections(optionTypeId, valueId);
    if (targetMember) {
      router.push(`${basePath}/${targetMember.slug}`);
    }
  };

  if (!currentMember) return null;

  return (
    <Styled.Container data-testid="product-variant-group-selector">
      {productGroup.optionTypes.map(optionType => {
        const values = getUniqueValuesForOptionType(optionType);
        if (values.length <= 1) return null;

        return (
          <Styled.OptionGroup key={optionType.id}>
            <Styled.Label>{optionType.presentation}</Styled.Label>
            <Styled.ThumbnailList>
              {values.map(value => (
                <Styled.ThumbnailItem
                  key={value.id}
                  $isActive={value.isActive}
                  $isDisabled={value.isDisabled}
                  role="button"
                  tabIndex={value.isDisabled ? -1 : 0}
                  aria-disabled={value.isDisabled}
                  onClick={() =>
                    !value.isDisabled &&
                    handleOptionClick(optionType.id, value.id)
                  }
                  onKeyDown={e => {
                    if (value.isDisabled) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOptionClick(optionType.id, value.id);
                    }
                  }}
                  data-testid={`variant-option-${optionType.id}-${value.id}`}
                >
                  <Styled.ThumbnailImage
                    src={value.imageUrl}
                    alt={value.presentation}
                    $isActive={value.isActive}
                  />
                  <Styled.OptionLabel title={value.presentation}>
                    {value.presentation}
                  </Styled.OptionLabel>
                </Styled.ThumbnailItem>
              ))}
            </Styled.ThumbnailList>
          </Styled.OptionGroup>
        );
      })}
    </Styled.Container>
  );
};

export default ProductVariantGroupSelector;
